"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InMemoryAdapter = void 0;
const adapter_error_js_1 = require("../errors/adapter-error.js");
/**
 * InMemoryAdapter — for DataService conformance tests only.
 *
 * Limitations (by design):
 * - `query()` and `execute()` do NOT parse SQL.  Tests must register
 *   expected results via `registerQueryHandler()` / `setTable()`.
 * - SQL-heavy integration tests should target ServerDataAdapter or
 *   SQLiteClientAdapter instead.
 * - `transaction()` executes the function immediately without true
 *   atomicity — it is sufficient for service-layer unit tests where
 *   the adapter is a test double.
 */
class InMemoryAdapter {
    tables = new Map();
    queryHandlers = new Map();
    // ---------------------------------------------------------------------------
    // Test-fixture helpers
    // ---------------------------------------------------------------------------
    /**
     * Replace the entire in-memory contents of a named table.
     * Keys are row identifiers (e.g. the row's `id` column value).
     */
    setTable(tableName, rows) {
        this.tables.set(tableName, new Map(rows));
    }
    /**
     * Retrieve all rows stored for a named table.
     * Returns an empty Map if the table has never been set.
     */
    getTable(tableName) {
        return new Map(this.tables.get(tableName) ?? []);
    }
    /**
     * Register a handler for a specific SQL string.
     * The handler receives the params array and returns an array of rows.
     * Use this when a DataService method issues a fixed SQL statement and
     * you want to intercept it without full SQL parsing.
     */
    registerQueryHandler(sql, handler) {
        this.queryHandlers.set(sql, handler);
    }
    /** Remove all registered handlers (useful between test cases). */
    clearHandlers() {
        this.queryHandlers.clear();
    }
    // ---------------------------------------------------------------------------
    // DataAdapter implementation
    // ---------------------------------------------------------------------------
    async query(sql, params = []) {
        const handler = this.queryHandlers.get(sql);
        if (handler) {
            return handler(params);
        }
        throw new adapter_error_js_1.AdapterError(`InMemoryAdapter: no query handler registered for SQL:\n  ${sql}\n` +
            `Register one via registerQueryHandler() or use setTable() for simple lookups.`);
    }
    async execute(sql, _params = []) {
        const handler = this.queryHandlers.get(sql);
        if (handler) {
            handler(_params);
            return { changes: 1 };
        }
        throw new adapter_error_js_1.AdapterError(`InMemoryAdapter: no execute handler registered for SQL:\n  ${sql}\n` +
            `Register one via registerQueryHandler().`);
    }
    /**
     * Executes fn immediately.  No real transaction semantics —
     * suitable only for unit tests where fn itself is a test double.
     */
    async transaction(fn) {
        return fn();
    }
    async close() {
        this.tables.clear();
        this.queryHandlers.clear();
    }
}
exports.InMemoryAdapter = InMemoryAdapter;
//# sourceMappingURL=in-memory.adapter.js.map